<template>
  <div
    id="settingsMenu"
    v-on-clickaway:mousedown="closeFlyout"
    class="user-context-menu"
  >
    <button
      class="user-context-menu__button"
      type="button"
      data-e2e-id="user-context-menu__button"
      @click="toggleFlyout"
    >
      <div class="user-container">
        <div class="user-container__icon">
          {{ currentUserInitials }}
        </div>
        <div class="user-container__name">
          <div
            class="full-name subtitle-2"
          >
            {{ nameDisplay }}
          </div>
          <div
            class="dealership-name caption"
          >
            {{ dealershipName }}
          </div>
        </div>
        <acv-icon
          class="user-container__menu-icon"
          icon="menu"
        />
      </div>
    </button>
    <div
      v-if="isFlyoutOpen"
      class="user-context-menu__flyout"
      :style="menuFlyoutStyles"
    >
      <div class="user-context-menu__flyout__inner">
        <router-link
          v-for="menuOption in menuOptions"
          :key="menuOption.label"
          class="user-context-menu__flyout__link"
          :to="menuOption.to ?? '/'"
        >
          <acv-list-button
            role="menuitem"
            :label="menuOption.label"
            :class="menuOption.class"
            :data-e2e-id="menuOption.class"
            @click="handleMenuOptionClicked(menuOption)"
            @keypress.enter="handleMenuOptionClicked(menuOption)"
          />
        </router-link>
        <acv-divider />
        <router-link
          class="user-context-menu__flyout__link"
          to="/logout"
          @click.native="handleLogout"
        >
          <acv-button
            class="logout-button"
            data-e2e-id="logout-button"
          >
            Logout
          </acv-button>
        </router-link>
      </div>
    </div>
  </div>
</template>

<script>
import {
  AcvIcon, AcvListButton, AcvDivider, AcvButton,
} from '@acv/shared-vuejs';
import Analytics from '@/mixins/Analytics';
import { directive as onClickaway } from 'vue-clickaway2';

export default {
  name: 'AcvSearchSettingsDropdownMenu',
  components: {
    AcvIcon,
    AcvListButton,
    AcvDivider,
    AcvButton,
  },
  directives: {
    onClickaway,
  },
  mixins: [
    Analytics,
  ],
  props: {
    fullName: {
      type: String,
      default: '',
    },
    dealershipName: {
      type: String,
      default: '',
    },
  },
  data: () => ({
    isFlyoutOpen: false,
    menuFlyoutStyles: {},
  }),
  computed: {
    nameDisplay() {
      return this.fullName || 'No Name Available';
    },
    currentUserInitials() {
      return this.fullName.slice(0, 1);
    },
    menuOptions() {
      let menuOptions = [
        {
          label: 'Settings',
          class: 'settings-link',
          to: '/settings/account',
        },
      ];

      if (this.$route.name === 'Search') {
        menuOptions = [
          ...menuOptions,
          ...[
            {
              label: 'Tutorials',
              class: 'tutorial-link',
              click: this.handleRevisitToursClicked,
            },
          ],
        ];
      }

      return menuOptions;
    },
  },
  created() {
    this.$root.$on('acv::settings-menu::show', this.showFlyout);
    window.addEventListener('resize', this.calculateFlyoutPositioning);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.calculateFlyoutPositioning);
  },
  methods: {
    toggleFlyout() {
      if (!this.isFlyoutOpen) {
        this.showFlyout();
        this.calculateFlyoutPositioning();
      } else {
        this.closeFlyout();
      }
    },

    showFlyout() {
      if (!this.isFlyoutOpen) {
        this.isFlyoutOpen = true;
      }
    },
    closeFlyout() {
      if (this.isFlyoutOpen) {
        this.isFlyoutOpen = false;
      }
    },

    calculateFlyoutPositioning() {
      window.requestAnimationFrame(() => {
        const paddingRight = 24; // filter bar right side spacing
        const buttonSelector = document.querySelector('.user-context-menu__button');
        const flyoutElement = document.querySelector('.user-context-menu__flyout');
        if (flyoutElement) {
          const { right: buttonRight } = buttonSelector.getBoundingClientRect();
          const windowWidth = window.innerWidth || document.documentElement.clientWidth;

          // if button right goes beyond viewport, then result is positive, else negative
          const diffRight = buttonRight - windowWidth;

          this.menuFlyoutStyles = {
            right: `${diffRight + paddingRight}px`,
          };
        }
      });
    },

    handleMenuOptionClicked(menuOption) {
      if (typeof menuOption.click === 'function') {
        menuOption.click.call(menuOption);
      } else {
        this.$router.push(menuOption.to);
      }
      this.trackMenuItemSelected(menuOption.label);
      this.closeFlyout();
    },

    handleLogout() {
      this.closeFlyout();
      this.trackMenuItemSelected('Logout');
    },

    trackMenuItemSelected(item) {
      this.track(this.eventNames.userMenuSelected, {
        item_selected: item,
      });
    },

    handleRevisitToursClicked() {
      this.$root.$emit('acv::show-search-tour-list');
    },
  },
};
</script>

<style lang="scss" scoped>
@use '@/scss/search/fonts';

.user-context-menu {
  position: relative;

  &__button {
    display: inline-flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    padding: 0.375rem 0.75rem;
    background-color: theme('colors.background.white');
    color: theme('colors.black.500');
    border: none;
    border-radius: 0.5rem;
    white-space: nowrap;

    &:hover {
      background-color: theme('colors.background.dark');
    }

    &:focus-visible {
      outline: none;
      outline: 2px solid theme('colors.focus.base');
      outline-offset: -2px;
    }

    .user-container {
      display: flex;
      align-items: center;

      &__icon {
        display: flex;
        align-items: center;
        justify-content: center;
        background: theme('colors.primary.base');
        color: theme('colors.text.white');
        font-weight: theme('fontWeight.medium');
        height: 1.875rem;
        width: 1.875rem;
        line-height: 1.125rem;
        border-radius: 100px;
        text-transform: capitalize;
      }

      &__name {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        margin-left: 0.75rem;
        margin-right: 1.25rem;
        line-height: 1.125rem;
      }

      &__menu-icon {
        font-size: 1.5rem;
      }
    }
  }

  &__flyout {
    position: absolute;
    margin: 0.25rem 0 0 ;
    z-index: 1000;
    width: max-content;
    background: theme('colors.background.white');
    border-radius: 0.75rem;
    box-shadow: 0 0 2px 0 rgb(132 132 132 / 14%),
      0 2px 2px 0 rgb(132 132 132 / 12%),
      0 1px 3px 0 rgb(132 132 132 / 20%);

    &__inner {
      padding: 1.25rem 1rem;
      display: flex;
      flex-direction: column;
    }

    &__link {
      width: 100%;

      + .user-context-menu__flyout__link {
        margin-top: 0.5rem;
      }

      button {
        width: 100%;
      }
    }
  }
}
</style>
